import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, FileText, Shield, Wallet, Share2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { formatINR } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";

const TYPES = ["1bhk","2bhk","3bhk","3bhk_plus","villa","studio","commercial"];
const TYPE_LABEL = { "1bhk":"1BHK","2bhk":"2BHK","3bhk":"3BHK","3bhk_plus":"3BHK+","villa":"Villa","studio":"Studio","commercial":"Commercial" };

// Agreement type commission for brokers/agents (flat amount in rupees)
const AGREEMENT_COMMISSION = {
  "e_agreement": 2500,      // ₹25 commission on ₹99 agreement
  "e_stamp": 5000,          // ₹50 commission on ₹299 agreement
  "traditional_paper": 0    // No commission on paper agreements
};

export default function NewRental() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isBrokerOrAgent = ["broker_admin", "agent"].includes(user?.role);
  const isLandlord = user?.role === "landlord";
  const [step, setStep] = useState(0);
  const [agreementType, setAgreementType] = useState(null);       // "e_agreement" | "e_stamp" | "traditional_paper"
  const [depositProtection, setDepositProtection] = useState(null); // true | false
  const [proofService, setProofService] = useState(false);          // ₹1299 add-on when no deposit protection
  const [feePayer, setFeePayer] = useState(null);                  // "tenant" | "landlord" | "split"
  const [brokerageFromTenant, setBrokerageFromTenant] = useState("");
  const [brokerageFromLandlord, setBrokerageFromLandlord] = useState("");
  const [form, setForm] = useState({ property_address: "", property_type: "2bhk", property_category: "flat", property_area_sqft: "", monthly_rent: "", deposit: "", lease_start_date: "", lease_end_date: "", lock_in_months: 6, tenant_phone: "", landlord_phone: "", tenant_permanent_address: "", landlord_permanent_address: "" });
  const [extraTenants, setExtraTenants] = useState([]); // [{phone, share}]
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [tenantRegistered, setTenantRegistered] = useState(true);
  const [landlordRegistered, setLandlordRegistered] = useState(true);
  const [shareModal, setShareModal] = useState(null);  // null | "tenant" | "landlord"
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Indian mobile: exactly 10 digits, first digit 6-9
  const isValidIndianMobile = (num) => /^[6-9]\d{9}$/.test(num.replace(/\D/g, "").slice(-10));
  const phoneDigits = (num) => num.replace(/\D/g, "").replace(/^91/, ""); // strip country code if pasted
  const PhoneError = ({ value }) => {
    if (!value) return null;
    const digits = phoneDigits(value);
    if (digits.length < 10) return <p className="text-xs text-red-500 mt-1">Enter all 10 digits.</p>;
    if (digits.length > 10) return <p className="text-xs text-red-500 mt-1">Too many digits — enter exactly 10.</p>;
    if (!/^[6-9]/.test(digits)) return <p className="text-xs text-red-500 mt-1">Indian mobile must start with 6, 7, 8, or 9.</p>;
    return null;
  };
  const allPhonesValid =
    isValidIndianMobile(form.tenant_phone) &&
    isValidIndianMobile(form.landlord_phone) &&
    extraTenants.every(t => !t.phone || isValidIndianMobile(t.phone));

  const depositPaiseTotal = Math.round(Number(form.deposit) * 100 || 0);

  const addExtraTenant = () => {
    if (extraTenants.length >= 3) return;
    setExtraTenants([...extraTenants, { phone: "" }]);
  };
  const removeExtraTenant = (i) => setExtraTenants(extraTenants.filter((_, idx) => idx !== i));
  const updateExtraTenantPhone = (i, value) => {
    const updated = extraTenants.map((t, idx) => idx === i ? { ...t, phone: value } : t);
    setExtraTenants(updated);
  };

  // Check if users are registered when entering review page
  useEffect(() => {
    if (step === 3 && form.tenant_phone && form.landlord_phone) {
      (async () => {
        try {
          const response = await api.post("/users/check-registration", {
            tenant_phone: form.tenant_phone,
            landlord_phone: form.landlord_phone,
          });
          setTenantRegistered(response.data.tenant_registered === true);
          setLandlordRegistered(response.data.landlord_registered === true);
        } catch (err) {
          console.error("Error checking registration:", err);
        }
      })();
    }
  }, [step, form.tenant_phone, form.landlord_phone]);
  const inputCls = "mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500";

  const fee = form.deposit ? Math.max(19900,
    depositPaiseTotal <= 15000000 ? Math.floor(depositPaiseTotal * 0.005) :
    depositPaiseTotal <= 150000000 ? Math.floor(depositPaiseTotal * 0.007) :
    Math.floor(depositPaiseTotal * 0.01)
  ) : 0;

  const agreementLabel = agreementType === "e_agreement" ? "E-Agreement" : agreementType === "e_stamp" ? "E-Stamp Agreement" : "Traditional Paper Agreement";
  const agreementPrice = agreementType === "e_agreement" ? "₹199 flat" : agreementType === "e_stamp" ? "₹299 + stamp duty" : "Paper-based";
  const feePayerLabel = feePayer === "tenant" ? "Tenant pays full fee" : feePayer === "landlord" ? "Landlord pays full fee" : feePayer === "split" ? "Split 50-50" : "";

  // An agreement is fee-bearing if it carries an Orello agreement fee
  // (e-stamp / e-agreement always do, regardless of deposit protection) or if
  // the ₹1,299 dispute-resolution add-on was taken. Traditional paper has no
  // agreement fee of its own, so it only becomes fee-bearing via the add-on.
  const isFeeBearing = () =>
    agreementType === "e_agreement" ||
    agreementType === "e_stamp" ||
    (agreementType === "traditional_paper" && depositProtection === false && proofService);

  // Step 0 → next: only ask who pays when there's actually a fee to allocate.
  const afterStep0 = () => {
    if (isFeeBearing()) setStep(0.5);
    else setStep(1);
  };

  // Step 0.5 → back goes to step 0
  const backFromFeePayer = () => setStep(0);

  const generateReferralLink = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/?ref=${user?.id}`;
  };

  const shareReferralLink = (channel, partyType) => {
    const referralLink = generateReferralLink();
    const phone = partyType === "tenant" ? form.tenant_phone : form.landlord_phone;
    const partyLabel = partyType === "tenant" ? "Tenant" : "Owner";
    const message = `Hi! I've created a rental agreement for you on Orello. Please join using this link to review and sign: ${referralLink}`;

    // Copy link to clipboard
    navigator.clipboard.writeText(referralLink).then(() => {
      toast.success("Referral link copied to clipboard!");
    }).catch(() => {
      toast.error("Failed to copy link");
    });

    // Open the respective app/client
    if (channel === "whatsapp") {
      const whatsappUrl = `https://wa.me/${phone.replace(/\D/g, "")}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, "_blank");
    } else if (channel === "sms") {
      const smsUrl = `sms:${phone}?body=${encodeURIComponent(message)}`;
      window.location.href = smsUrl;
    } else if (channel === "email") {
      const emailUrl = `mailto:?subject=Join my Orello rental agreement&body=${encodeURIComponent(message)}`;
      window.location.href = emailUrl;
    }

    setShareModal(null);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const fmt = (num) => `+91${phoneDigits(num)}`;
      const tp = fmt(form.tenant_phone);
      const lp = fmt(form.landlord_phone);
      const extraFormatted = extraTenants
        .filter(t => t.phone && isValidIndianMobile(t.phone))
        .map(t => ({ phone: fmt(t.phone) }));
      const payload = {
        property_address: form.property_address,
        property_type: form.property_type,
        property_category: form.property_category || null,
        property_area_sqft: form.property_area_sqft ? Number(form.property_area_sqft) : null,
        monthly_rent: Math.round(Number(form.monthly_rent) * 100),
        deposit_amount: depositPaiseTotal,
        lease_start_date: form.lease_start_date,
        lease_end_date: form.lease_end_date,
        lock_in_months: Number(form.lock_in_months),
        tenant_phone: tp,
        landlord_phone: lp,
        tenant_permanent_address: form.tenant_permanent_address || null,
        landlord_permanent_address: form.landlord_permanent_address || null,
        extra_tenants: extraFormatted,
        agreement_type: agreementType,
        deposit_protection: depositProtection,
        proof_service: depositProtection === false ? proofService : false,
        fee_payer: depositProtection ? feePayer : (proofService ? feePayer : null),
        brokerage_from_tenant: brokerageFromTenant ? Math.round(Number(brokerageFromTenant) * 100) : 0,
        brokerage_from_landlord: brokerageFromLandlord ? Math.round(Number(brokerageFromLandlord) * 100) : 0,
      };
      const { data } = await api.post("/rentals", payload);
      setCreated(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (created) {
    const hasPendingRegistration = created.status === "onboarding_pending";

    return (
      <div className="max-w-xl mx-auto py-12 text-center" data-testid="rental-success">
        <div className="h-20 w-20 mx-auto rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center"><Check className="h-10 w-10" /></div>
        <h1 className="mt-5 text-3xl font-bold font-display">Rental created!</h1>
        <p className="text-gray-600 mt-2">
          {hasPendingRegistration
            ? "Agreement created. Share your referral link with tenant and owner so they can register and sign."
            : "Invites sent to tenant and owner. They'll receive a WhatsApp message to join."}
        </p>

        {/* Role-specific messaging */}
        {isBrokerOrAgent && (
          <div className="mt-5 bg-orange-50 border border-orange-200 rounded-xl p-4">
            <p className="text-sm text-gray-900"><strong>Your earning opportunity:</strong> Once your tenant completes the deposit payment with Orello, you'll earn up to ₹1,000 — automatically credited to your wallet.</p>
          </div>
        )}

        {isLandlord && (
          <div className="mt-5 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <p className="text-sm text-gray-900">Your agreement is live. Orello will protect this deposit and handle any disputes — so you don't have to.</p>
          </div>
        )}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <div className="card-base p-4 text-left">
            <div className="text-xs text-gray-500 uppercase">Tenant</div>
            <div className="font-medium mt-1">{created.tenants_resolved?.[0]?.user?.name || "Pending registration"}</div>
            <div className={`text-xs mt-1 ${created.tenants_resolved?.[0]?.user ? "text-amber-600" : "text-orange-600"}`}>
              {created.tenants_resolved?.[0]?.user ? "Waiting to sign agreement" : "Onboarding pending"}
            </div>
          </div>
          <div className="card-base p-4 text-left">
            <div className="text-xs text-gray-500 uppercase">Owner</div>
            <div className="font-medium mt-1">{created.landlord?.name || "Pending registration"}</div>
            <div className={`text-xs mt-1 ${created.landlord ? "text-amber-600" : "text-orange-600"}`}>
              {created.landlord ? "Waiting to sign agreement" : "Onboarding pending"}
            </div>
          </div>
        </div>
        <button onClick={() => nav(`/app/rental/${created.id}`)} className="mt-6 bg-[#6366F1] hover:bg-[#4F46E5] btn-press text-white px-6 py-3 rounded-lg font-medium" data-testid="goto-deal">Go to Deal →</button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6" data-testid="new-rental-page">

      {/* Progress bar — only show from step 1 onwards */}
      {step >= 1 && (
        <div>
          <div className="flex items-center gap-2 text-xs text-gray-500 uppercase tracking-wider">
            <span className={step >= 1 ? "text-indigo-600 font-semibold" : ""}>1. Property</span>
            <span>›</span>
            <span className={step >= 2 ? "text-indigo-600 font-semibold" : ""}>2. People</span>
            <span>›</span>
            <span className={step >= 3 ? "text-indigo-600 font-semibold" : ""}>3. Review</span>
          </div>
          <div className="h-1 bg-gray-100 rounded-full mt-2 overflow-hidden">
            <div className="h-1 bg-indigo-500 transition-all" style={{ width: `${((step) / 3) * 100}%` }} />
          </div>
        </div>
      )}

      {/* ── STEP 0 — Agreement type + Deposit protection ── */}
      {step === 0 && (
        <div className="card-base p-6 space-y-8">
          <div>
            <h1 className="text-2xl font-bold font-display">Set up this deal</h1>
            <p className="text-sm text-gray-500 mt-1">Answer two quick questions to get started.</p>
          </div>

          {/* Question 1 — Agreement type */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-indigo-500" />
              <span className="text-sm font-semibold text-gray-800">What type of agreement?</span>
            </div>
            <div className="space-y-2">
              <label onClick={() => { setAgreementType("e_agreement"); setDepositProtection(null); }} className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${agreementType === "e_agreement" ? "border-indigo-500 bg-indigo-50" : "border-gray-200 hover:border-gray-300"}`}>
                <div className={`mt-0.5 h-4 w-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${agreementType === "e_agreement" ? "border-indigo-500 bg-indigo-500" : "border-gray-300"}`}>
                  {agreementType === "e_agreement" && <Check className="h-3 w-3 text-white" />}
                </div>
                <div className="flex-1">
                  <div className={`text-sm font-medium ${agreementType === "e_agreement" ? "text-indigo-900" : "text-gray-900"}`}>E-Agreement</div>
                  <div className={`text-xs ${agreementType === "e_agreement" ? "text-indigo-700" : "text-gray-500"} mt-0.5`}>Digital rental agreement • ₹199 flat</div>
                  {isBrokerOrAgent && <div className={`text-xs font-medium mt-2 ${agreementType === "e_agreement" ? "text-[#F97316]" : "text-[#F97316]"}`}>Earn Flat ₹25 commission</div>}
                </div>
              </label>
              <label onClick={() => { setAgreementType("e_stamp"); setDepositProtection(null); }} className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${agreementType === "e_stamp" ? "border-indigo-500 bg-indigo-50" : "border-gray-200 hover:border-gray-300"}`}>
                <div className={`mt-0.5 h-4 w-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${agreementType === "e_stamp" ? "border-indigo-500 bg-indigo-500" : "border-gray-300"}`}>
                  {agreementType === "e_stamp" && <Check className="h-3 w-3 text-white" />}
                </div>
                <div className="flex-1">
                  <div className={`text-sm font-medium ${agreementType === "e_stamp" ? "text-indigo-900" : "text-gray-900"}`}>E-Stamp Agreement</div>
                  <div className={`text-xs ${agreementType === "e_stamp" ? "text-indigo-700" : "text-gray-500"} mt-0.5`}>Legally stamped agreement • ₹299 + stamp duty</div>
                  {isBrokerOrAgent && <div className={`text-xs font-medium mt-2 ${agreementType === "e_stamp" ? "text-[#F97316]" : "text-[#F97316]"}`}>Earn Flat ₹50 commission</div>}
                </div>
              </label>
              <label onClick={() => { setAgreementType("traditional_paper"); setDepositProtection(null); }} className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${agreementType === "traditional_paper" ? "border-indigo-500 bg-indigo-50" : "border-gray-200 hover:border-gray-300"}`}>
                <div className={`mt-0.5 h-4 w-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${agreementType === "traditional_paper" ? "border-indigo-500 bg-indigo-500" : "border-gray-300"}`}>
                  {agreementType === "traditional_paper" && <Check className="h-3 w-3 text-white" />}
                </div>
                <div className="flex-1">
                  <div className={`text-sm font-medium ${agreementType === "traditional_paper" ? "text-indigo-900" : "text-gray-900"}`}>Traditional Paper Agreement</div>
                  <div className={`text-xs ${agreementType === "traditional_paper" ? "text-indigo-700" : "text-gray-500"} mt-0.5`}>Paper-based agreement • deposit protection optional</div>
                  {isBrokerOrAgent && <div className={`text-xs font-medium mt-2 text-[#F97316]`}>Earn on deposit protection or dispute resolution</div>}
                </div>
              </label>
            </div>
          </div>

          {/* Earnings callout for brokers/agents */}
          {isBrokerOrAgent && agreementType && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-start gap-3">
              <span className="text-lg mt-0.5">💰</span>
              <div>
                <div className="text-sm font-semibold text-gray-900">Your earnings on this agreement</div>
                <div className="space-y-1.5 mt-2">
                  {agreementType !== "traditional_paper" && <p className="text-xs text-gray-700"><strong>Agreement commission:</strong> ₹{(AGREEMENT_COMMISSION[agreementType] / 100).toFixed(0)}</p>}
                  {depositProtection && <p className="text-xs text-gray-700"><strong>Deposit earnings:</strong> Up to ₹1,000 (when tenant pays deposit)</p>}
                  {depositProtection === false && proofService && <p className="text-xs text-gray-700"><strong>Proof service commission:</strong> ₹100 (on the ₹1,299 add-on)</p>}
                  <p className="text-xs text-gray-600 mt-2">Actual earnings calculated based on the deposit amount when tenant pays.</p>
                </div>
              </div>
            </div>
          )}

          {/* Question 2 — Deposit protection (offered for every agreement type;
              traditional paper can now proceed with or without a held deposit) */}
          {agreementType && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-indigo-500" />
              <span className="text-sm font-semibold text-gray-800 dark:text-white">Add deposit protection?</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 -mt-1">Orello holds the deposit safely and handles disputes and settlement.</p>
            <div className="space-y-2">
              <label onClick={() => setDepositProtection(true)} className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${depositProtection === true ? "border-indigo-500 bg-indigo-50" : "border-gray-200 hover:border-gray-300"}`}>
                <div className={`mt-0.5 h-4 w-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${depositProtection === true ? "border-indigo-500 bg-indigo-500" : "border-gray-300"}`}>
                  {depositProtection === true && <Check className="h-3 w-3 text-white" />}
                </div>
                <div className="flex-1">
                  <div className={`text-sm font-medium ${depositProtection === true ? "text-indigo-900" : "text-gray-900"}`}>Yes</div>
                  <div className={`text-xs ${depositProtection === true ? "text-indigo-700" : "text-gray-500"} mt-0.5`}>Tiered Orello fee applies</div>
                </div>
              </label>
              <label onClick={() => setDepositProtection(false)} className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${depositProtection === false ? "border-indigo-500 bg-indigo-50" : "border-gray-200 hover:border-gray-300"}`}>
                <div className={`mt-0.5 h-4 w-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${depositProtection === false ? "border-indigo-500 bg-indigo-500" : "border-gray-300"}`}>
                  {depositProtection === false && <Check className="h-3 w-3 text-white" />}
                </div>
                <div className="flex-1">
                  <div className={`text-sm font-medium ${depositProtection === false ? "text-indigo-900" : "text-gray-900"}`}>No</div>
                  <div className={`text-xs ${depositProtection === false ? "text-indigo-700" : "text-gray-500"} mt-0.5`}>Agreement only</div>
                </div>
              </label>
            </div>
          </div>

          )}
          {depositProtection === true && (
            <div className="p-4 rounded-xl bg-[#FFF4ED] dark:bg-orange-950 border border-orange-200 dark:border-orange-800 flex items-center gap-3">
              <Shield className="h-5 w-5 text-[#F97316] flex-shrink-0" />
              <div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">Deposit protection enabled</div>
                <div className="text-xs text-gray-700 dark:text-gray-300 mt-0.5">Orello holds the deposit safely and handles all disputes and settlement.</div>
              </div>
            </div>
          )}

          {/* Proof service add-on — only when deposit protection is declined */}
          {(agreementType === "e_agreement" || agreementType === "e_stamp") && depositProtection === false && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-indigo-500" />
                <span className="text-sm font-semibold text-gray-800 dark:text-white">Add condition reports + dispute proof? <span className="text-[#F97316]">₹1,299</span></span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                The landlord keeps control of the deposit, but Orello still provides check-in / checkout condition
                reports and ORE-AI dispute resolution. These become documented, court-usable proof if there's a
                deposit dispute later. One-time ₹1,299.
              </p>
              <label onClick={() => { const nv = !proofService; setProofService(nv); if (!nv) setFeePayer(null); }}
                className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${proofService ? "border-indigo-500 bg-indigo-50" : "border-gray-200 hover:border-gray-300"}`}>
                <div className={`mt-0.5 h-4 w-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${proofService ? "border-indigo-500 bg-indigo-500" : "border-gray-300"}`}>
                  {proofService && <Check className="h-3 w-3 text-white" />}
                </div>
                <div className="flex-1">
                  <div className={`text-sm font-medium ${proofService ? "text-indigo-900" : "text-gray-900"}`}>Yes, add proof service (₹1,299)</div>
                  <div className={`text-xs ${proofService ? "text-indigo-700" : "text-gray-500"} mt-0.5`}>Condition reports + dispute resolution, as court proof</div>
                </div>
              </label>

              {proofService && (
                <div className="pt-1 space-y-2">
                  <div className="text-xs font-medium text-gray-700 dark:text-gray-300">Who pays the ₹1,299?</div>
                  <div className="grid grid-cols-3 gap-2">
                    {[["landlord", "Landlord"], ["tenant", "Tenant"], ["split", "50-50 split"]].map(([val, label]) => (
                      <button key={val} type="button" onClick={() => setFeePayer(val)}
                        className={`px-3 py-2 rounded-lg text-xs font-medium border-2 transition-all ${feePayer === val ? "border-indigo-500 bg-indigo-50 text-indigo-900" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end">
            <button onClick={afterStep0} disabled={agreementType === null || depositProtection === null} data-testid="step0-next" className="bg-[#6366F1] hover:bg-[#4F46E5] btn-press text-white px-5 py-2.5 rounded-lg text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
              Next <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 0.5 — Who pays the Orello fee ── */}
      {step === 0.5 && (
        <div className="card-base p-6 space-y-6">
          <div>
            <h1 className="text-2xl font-bold font-display">Who pays the Orello fee?</h1>
            <p className="text-sm text-gray-500 mt-1">The Orello deposit protection fee — decide who bears the cost.</p>
          </div>

          {isLandlord && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
              <span className="text-lg mt-0.5">🏠</span>
              <div>
                <div className="text-sm font-semibold text-gray-900">Your deposit, fully protected</div>
                <p className="text-xs text-gray-700 mt-1">Orello will protect this deposit with condition reporting, dispute resolution, and a clear paper trail — so you never lose a rupee you're owed.</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-indigo-500" />
            <span className="text-sm font-semibold text-gray-800">Choose one</span>
          </div>

          <div className="space-y-3">
            <label onClick={() => setFeePayer("tenant")} className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${feePayer === "tenant" ? "border-indigo-500 bg-indigo-50" : "border-gray-200 hover:border-gray-300"}`}>
              <div className={`mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${feePayer === "tenant" ? "border-indigo-500" : "border-gray-300"}`}>
                {feePayer === "tenant" && <div className="h-2 w-2 rounded-full bg-indigo-500" />}
              </div>
              <div>
                <div className={`text-sm font-medium ${feePayer === "tenant" ? "text-indigo-900" : "text-gray-900"}`}>Tenant pays full fee</div>
                <div className={`text-xs ${feePayer === "tenant" ? "text-indigo-700" : "text-gray-500"} mt-0.5`}>Tenant pays deposit + {agreementLabel} fee</div>
              </div>
            </label>

            <label onClick={() => setFeePayer("landlord")} className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${feePayer === "landlord" ? "border-indigo-500 bg-indigo-50" : "border-gray-200 hover:border-gray-300"}`}>
              <div className={`mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${feePayer === "landlord" ? "border-indigo-500" : "border-gray-300"}`}>
                {feePayer === "landlord" && <div className="h-2 w-2 rounded-full bg-indigo-500" />}
              </div>
              <div>
                <div className={`text-sm font-medium ${feePayer === "landlord" ? "text-indigo-900" : "text-gray-900"}`}>Landlord pays full fee</div>
                <div className={`text-xs ${feePayer === "landlord" ? "text-indigo-700" : "text-gray-500"} mt-0.5`}>Landlord bears the {agreementLabel} fee separately</div>
              </div>
            </label>

            <label onClick={() => setFeePayer("split")} className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${feePayer === "split" ? "border-indigo-500 bg-indigo-50" : "border-gray-200 hover:border-gray-300"}`}>
              <div className={`mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${feePayer === "split" ? "border-indigo-500" : "border-gray-300"}`}>
                {feePayer === "split" && <div className="h-2 w-2 rounded-full bg-indigo-500" />}
              </div>
              <div>
                <div className={`text-sm font-medium ${feePayer === "split" ? "text-indigo-900" : "text-gray-900"}`}>Split 50-50</div>
                <div className={`text-xs ${feePayer === "split" ? "text-indigo-700" : "text-gray-500"} mt-0.5`}>Tenant and landlord each pay half the {agreementLabel} fee</div>
              </div>
            </label>
          </div>

          <div className="flex justify-between">
            <button onClick={backFromFeePayer} className="text-sm text-gray-600 inline-flex items-center gap-2"><ArrowLeft className="h-4 w-4" /> Back</button>
            <button onClick={() => setStep(1)} disabled={feePayer === null} data-testid="step05-next" className="bg-[#6366F1] hover:bg-[#4F46E5] btn-press text-white px-5 py-2.5 rounded-lg text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
              Next: Property details <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 1 — Property ── */}
      {step === 1 && (
        <div className="card-base p-6 space-y-5">
          <h1 className="text-2xl font-bold font-display">Where is the property?</h1>

          {/* PRE-PHASE 1: Property Verification Disclaimer */}
          <div className="bg-amber-50 border border-amber-100 rounded-lg px-4 py-3 flex items-start gap-3 text-sm">
            <div className="text-amber-600 mt-0.5">ⓘ</div>
            <p className="text-amber-700">
              <strong>Important:</strong> Orello does not verify property ownership or legitimacy. All parties are responsible for due diligence regarding the property.
            </p>
          </div>
          <div><label className="text-sm font-medium">Full address</label><textarea data-testid="nr-address" required value={form.property_address} onChange={set("property_address")} placeholder="Building name, street, area, city" className={inputCls} rows={2} /></div>
          <div>
            <label className="text-sm font-medium">Property type</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {TYPES.map(t => (
                <button key={t} type="button" onClick={() => setForm({...form, property_type: t})} data-testid={`type-${t}`} className={`px-3 py-1.5 rounded-lg text-sm border ${form.property_type === t ? "bg-indigo-50 border-indigo-500 text-indigo-900" : "border-gray-200 text-gray-600"}`}>{TYPE_LABEL[t]}</button>
              ))}
            </div>
          </div>

          {/* Property category + area */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Property category</label>
              <div className="mt-2 flex gap-2">
                {[
                  { value: "flat",       label: "Flat" },
                  { value: "house",      label: "House" },
                  { value: "commercial", label: "Commercial" },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setForm({ ...form, property_category: opt.value })}
                    className={`flex-1 py-1.5 rounded-lg text-sm border transition-colors ${
                      form.property_category === opt.value
                        ? "bg-indigo-50 border-indigo-500 text-indigo-900 font-medium"
                        : "border-gray-200 text-gray-600"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {form.property_category === "commercial" && (
                <p className="text-xs text-gray-500 mt-1">Shops, offices, showrooms, etc.</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Area <span className="text-gray-400 font-normal">(optional)</span></label>
              <div className="mt-1 flex items-stretch border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-indigo-100 focus-within:border-indigo-500">
                <input
                  type="number"
                  min="0"
                  value={form.property_area_sqft}
                  onChange={set("property_area_sqft")}
                  placeholder="e.g. 850"
                  className="flex-1 px-3 py-2.5 text-sm focus:outline-none"
                />
                <span className="px-3 flex items-center bg-gray-50 text-sm text-gray-500 border-l border-gray-300">sq ft</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-sm font-medium">Monthly rent (₹)</label><input data-testid="nr-rent" type="number" required value={form.monthly_rent} onChange={set("monthly_rent")} placeholder="e.g. 25000" className={inputCls} /></div>
            <div>
              <label className="text-sm font-medium">Security deposit (₹)</label>
              <input data-testid="nr-deposit" type="number" required value={form.deposit} onChange={set("deposit")} className={inputCls} />
              {depositProtection && fee > 0 && (
                <div className="mt-2 space-y-1.5">
                  {isBrokerOrAgent && (() => {
                    const flatCommission = AGREEMENT_COMMISSION[agreementType] || 0;
                    const depositEarnings = Math.floor(fee * 0.15);
                    const totalEarnings = flatCommission + depositEarnings;
                    return <p className="text-xs text-[#F97316] font-medium">💰 Earn ₹{(flatCommission / 100).toFixed(0)} (flat) + ₹{(depositEarnings / 100).toFixed(0)} (deposit) = ₹{(totalEarnings / 100).toFixed(0)} when tenant completes deposit payment</p>;
                  })()}
                  {isLandlord && <p className="text-xs text-emerald-600 font-medium">✅ This deposit will be protected by Orello from day one — condition reports, move-in/move-out documentation, and dispute resolution included.</p>}
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className="text-sm font-medium">Lease start</label><input data-testid="nr-start" type="date" required value={form.lease_start_date} onChange={set("lease_start_date")} className={inputCls} /></div>
            <div><label className="text-sm font-medium">Lease end</label><input data-testid="nr-end" type="date" required value={form.lease_end_date} onChange={set("lease_end_date")} className={inputCls} /></div>
            <div><label className="text-sm font-medium">Lock-in</label>
              <select value={form.lock_in_months} onChange={set("lock_in_months")} className={inputCls}>
                <option value={0}>None</option><option value={1}>1 month</option><option value={2}>2 months</option><option value={3}>3 months</option><option value={6}>6 months</option>
              </select>
            </div>
          </div>
          <div className="flex justify-between">
            <button onClick={() => isFeeBearing() ? setStep(0.5) : setStep(0)} className="text-sm text-gray-600 inline-flex items-center gap-2"><ArrowLeft className="h-4 w-4" /> Back</button>
            <button onClick={() => setStep(2)} disabled={!form.property_address || !form.monthly_rent || !form.deposit || !form.lease_start_date || !form.lease_end_date} data-testid="step1-next" className="bg-[#6366F1] hover:bg-[#4F46E5] btn-press text-white px-5 py-2.5 rounded-lg text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">Next: Add Tenant & Owner <ArrowRight className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      {/* ── STEP 2 — People ── */}
      {step === 2 && (
        <div className="card-base p-6 space-y-5">
          <h1 className="text-2xl font-bold font-display">Who's in this deal?</h1>

          {/* Primary tenant */}
          <div>
            <label className="text-sm font-medium">Tenant mobile</label>
            <p className="text-xs text-gray-500">Enter their mobile. We'll send them an invite.</p>
            <div className={`mt-1 flex items-stretch border rounded-lg overflow-hidden ${form.tenant_phone && !isValidIndianMobile(form.tenant_phone) ? "border-red-400" : "border-gray-300"}`}>
              <span className="px-3 flex items-center bg-gray-50 text-sm text-gray-500 border-r">+91</span>
              <input data-testid="nr-tenant" required value={form.tenant_phone} onChange={e => setForm({...form, tenant_phone: phoneDigits(e.target.value)})} placeholder="10-digit mobile" maxLength={10} className="flex-1 px-3 py-2.5 text-sm focus:outline-none" />
            </div>
            <PhoneError value={form.tenant_phone} />
          </div>

          <div>
            <label className="text-sm font-medium">Tenant permanent address (Aadhar)</label>
            <p className="text-xs text-gray-500">As mentioned in Aadhar card or government ID.</p>
            <textarea
              value={form.tenant_permanent_address}
              onChange={set("tenant_permanent_address")}
              placeholder="Full address as per Aadhar"
              className={inputCls}
              rows={2}
            />
          </div>

          {/* Co-tenants — same style as primary tenant field */}
          {extraTenants.map((et, i) => (
            <div key={i}>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium">Co-tenant {i + 1} mobile</label>
                  <p className="text-xs text-gray-500">Enter their mobile. Must have an Orello account.</p>
                </div>
                <button onClick={() => removeExtraTenant(i)} className="text-gray-400 hover:text-red-500 transition-colors ml-3 flex-shrink-0">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className={`mt-1 flex items-stretch border rounded-lg overflow-hidden ${et.phone && !isValidIndianMobile(et.phone) ? "border-red-400" : "border-gray-300"}`}>
                <span className="px-3 flex items-center bg-gray-50 text-sm text-gray-500 border-r">+91</span>
                <input
                  value={et.phone}
                  onChange={(e) => updateExtraTenantPhone(i, phoneDigits(e.target.value))}
                  placeholder="10-digit mobile"
                  maxLength={10}
                  className="flex-1 px-3 py-2.5 text-sm focus:outline-none"
                />
              </div>
              <PhoneError value={et.phone} />
            </div>
          ))}

          {/* Info — share chosen at payment */}
          {extraTenants.length > 0 && (
            <div className="rounded-xl bg-blue-50 border border-blue-100 px-4 py-3 flex items-start gap-2">
              <span className="text-blue-500 mt-0.5 flex-shrink-0">ⓘ</span>
              <p className="text-xs text-blue-700">
                Each tenant decides how much of the ₹{depositPaiseTotal > 0 ? (depositPaiseTotal / 100).toLocaleString("en-IN") : "—"} deposit they'll pay when they sign and pay. The last tenant is auto-assigned the remaining balance. The Orello fee is split equally.
              </p>
            </div>
          )}

          {/* Add co-tenant button */}
          {extraTenants.length < 3 && (
            <button
              onClick={addExtraTenant}
              className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors"
            >
              <Plus className="h-4 w-4" /> Add co-tenant
            </button>
          )}

          <div>
            <label className="text-sm font-medium">Owner mobile</label>
            <p className="text-xs text-gray-500">The property owner's mobile number.</p>
            <div className={`mt-1 flex items-stretch border rounded-lg overflow-hidden ${form.landlord_phone && !isValidIndianMobile(form.landlord_phone) ? "border-red-400" : "border-gray-300"}`}>
              <span className="px-3 flex items-center bg-gray-50 text-sm text-gray-500 border-r">+91</span>
              <input data-testid="nr-landlord" required value={form.landlord_phone} onChange={e => setForm({...form, landlord_phone: phoneDigits(e.target.value)})} placeholder="10-digit mobile" maxLength={10} className="flex-1 px-3 py-2.5 text-sm focus:outline-none" />
            </div>
            <PhoneError value={form.landlord_phone} />
          </div>

          <div>
            <label className="text-sm font-medium">Owner permanent address (Aadhar)</label>
            <p className="text-xs text-gray-500">As mentioned in Aadhar card or government ID.</p>
            <textarea
              value={form.landlord_permanent_address}
              onChange={set("landlord_permanent_address")}
              placeholder="Full address as per Aadhar"
              className={inputCls}
              rows={2}
            />
          </div>

          {/* Brokerage fields — hidden for self-managed landlords */}
          {user?.role !== "landlord" && (
            <div className="border-t border-gray-100 pt-4 space-y-4">
              <div>
                <label className="text-sm font-medium">Brokerage from tenant (₹) <span className="text-gray-400 font-normal">optional</span></label>
                <p className="text-xs text-gray-500">Dalali you are charging the tenant — recorded for your earnings.</p>
                <input type="number" value={brokerageFromTenant} onChange={(e) => setBrokerageFromTenant(e.target.value)} placeholder="e.g. 15000" className={inputCls} />
              </div>
              <div>
                <label className="text-sm font-medium">Brokerage from owner (₹) <span className="text-gray-400 font-normal">optional</span></label>
                <p className="text-xs text-gray-500">Dalali you are charging the landlord — recorded for your earnings.</p>
                <input type="number" value={brokerageFromLandlord} onChange={(e) => setBrokerageFromLandlord(e.target.value)} placeholder="e.g. 15000" className={inputCls} />
              </div>
            </div>
          )}
          <div className="flex justify-between">
            <button onClick={() => setStep(1)} className="text-sm text-gray-600 inline-flex items-center gap-2"><ArrowLeft className="h-4 w-4" /> Back</button>
            <button
              onClick={() => setStep(3)}
              disabled={!allPhonesValid || extraTenants.some(t => t.phone && !isValidIndianMobile(t.phone))}
              data-testid="step2-next"
              className="bg-[#6366F1] hover:bg-[#4F46E5] btn-press text-white px-5 py-2.5 rounded-lg text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
            >
              Review <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3 — Review ── */}
      {step === 3 && (
        <div className="space-y-5">
          <h1 className="text-2xl font-bold font-display">Review the details</h1>
          <div className="card-base p-5 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Agreement</span><span className="font-medium">{agreementLabel} — {agreementPrice}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Deposit protection</span><span className={`font-medium ${depositProtection ? "text-emerald-600" : "text-gray-500"}`}>{depositProtection ? "Yes — enabled" : "No"}</span></div>
            {depositProtection && <div className="flex justify-between"><span className="text-gray-500">Fee paid by</span><span className="font-medium">{feePayerLabel}</span></div>}
            <div className="border-t border-gray-100 my-1" />
            <div className="flex justify-between"><span className="text-gray-500">Address</span><span className="font-medium">{form.property_address}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Type</span><span className="font-medium">{TYPE_LABEL[form.property_type]}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Category</span><span className="font-medium capitalize">{form.property_category || "—"}</span></div>
            {form.property_area_sqft && <div className="flex justify-between"><span className="text-gray-500">Area</span><span className="font-medium">{Number(form.property_area_sqft).toLocaleString("en-IN")} sq ft</span></div>}
            <div className="flex justify-between"><span className="text-gray-500">Monthly rent</span><span className="font-medium">{formatINR(Number(form.monthly_rent) * 100)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Deposit</span><span className="font-medium">{formatINR(depositPaiseTotal)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Lease</span><span className="font-medium">{form.lease_start_date} → {form.lease_end_date}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Tenant 1</span><span className="font-medium">+91{form.tenant_phone}</span></div>
            {extraTenants.filter(t => t.phone).map((et, i) => (
              <div key={i} className="flex justify-between"><span className="text-gray-500">Co-tenant {i + 1}</span><span className="font-medium">+91{et.phone}</span></div>
            ))}
            <div className="flex justify-between"><span className="text-gray-500">Owner</span><span className="font-medium">+91{form.landlord_phone}</span></div>
            {(brokerageFromTenant || brokerageFromLandlord) && (
              <>
                <div className="border-t border-gray-100 my-1" />
                {brokerageFromTenant && <div className="flex justify-between"><span className="text-gray-500">Brokerage from tenant</span><span className="font-medium text-orange-600">{formatINR(Math.round(Number(brokerageFromTenant) * 100))}</span></div>}
                {brokerageFromLandlord && <div className="flex justify-between"><span className="text-gray-500">Brokerage from owner</span><span className="font-medium text-orange-600">{formatINR(Math.round(Number(brokerageFromLandlord) * 100))}</span></div>}
                <div className="flex justify-between"><span className="text-gray-500 font-medium">Total brokerage</span><span className="font-bold text-orange-600">{formatINR(Math.round(((Number(brokerageFromTenant)||0) + (Number(brokerageFromLandlord)||0)) * 100))}</span></div>
              </>
            )}
          </div>

          {/* Registration status messages with sharing options */}
          {!tenantRegistered && (
            <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-3 justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <span className="text-lg mt-0.5">👤</span>
                  <div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">Tenant not registered</div>
                    <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">Agreement cannot be shared until they create their Orello account.</p>
                    {isBrokerOrAgent && <p className="text-xs text-blue-700 dark:text-blue-300 mt-2 font-medium">💡 Earn referral commission when they complete deposit payment!</p>}
                  </div>
                </div>
                <button
                  onClick={() => setShareModal("tenant")}
                  className="ml-4 flex-shrink-0 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium text-sm flex items-center gap-2 whitespace-nowrap"
                >
                  <Share2 className="h-4 w-4" /> Share link
                </button>
              </div>
            </div>
          )}

          {!landlordRegistered && (
            <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-3 justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <span className="text-lg mt-0.5">🏠</span>
                  <div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">Owner not registered</div>
                    <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">Agreement cannot be shared until they create their Orello account.</p>
                    {isBrokerOrAgent && <p className="text-xs text-blue-700 dark:text-blue-300 mt-2 font-medium">💡 Earn referral commission when they complete deposit payment!</p>}
                  </div>
                </div>
                <button
                  onClick={() => setShareModal("landlord")}
                  className="ml-4 flex-shrink-0 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium text-sm flex items-center gap-2 whitespace-nowrap"
                >
                  <Share2 className="h-4 w-4" /> Share link
                </button>
              </div>
            </div>
          )}

          {depositProtection && isLandlord && (
            <div className="p-4 rounded-xl bg-[#FFF4ED] dark:bg-orange-950 border border-orange-200 dark:border-orange-800">
              <div className="text-sm font-semibold text-gray-900 dark:text-white">Fee breakdown</div>
              <div className="mt-3 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-gray-700 dark:text-gray-300">Deposit</span><span className="font-medium text-gray-900 dark:text-white">{formatINR(depositPaiseTotal)}</span></div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700 dark:text-gray-300">Orello protection fee ({Number(form.deposit) <= 150000 ? "0.5%" : Number(form.deposit) <= 1500000 ? "0.7%" : "1%"})</span>
                  <span className="font-medium text-gray-900 dark:text-white">{formatINR(fee)}</span>
                </div>
                {feePayer === "split" && (
                  <div className="text-xs text-orange-700 dark:text-orange-300 mt-1">Each party pays {formatINR(Math.ceil(fee / 2))}</div>
                )}
                <div className="flex justify-between text-sm border-t border-orange-200 dark:border-orange-700 pt-2 mt-2">
                  <span className="font-semibold text-gray-900 dark:text-white">{feePayer === "landlord" ? "Tenant pays" : feePayer === "split" ? "Tenant pays" : "Tenant pays total"}</span>
                  <span className="font-bold text-gray-900 dark:text-white">{feePayer === "landlord" ? "deposit only" : feePayer === "split" ? `deposit + ${formatINR(Math.ceil(fee / 2) / (1 + extraTenants.length))} fee each` : `deposit + ${formatINR(Math.round(fee / (1 + extraTenants.length)))} fee each`}</span>
                </div>
              </div>
              <p className="text-xs text-gray-700 dark:text-gray-300 mt-2.5">The fee keeps the deposit safe and covers the full rental process.</p>
            </div>
          )}

          {isBrokerOrAgent && (agreementType === "e_agreement" || agreementType === "e_stamp" || agreementType === "traditional_paper") && (
            <div className="p-4 rounded-xl bg-[#FFF4ED] dark:bg-orange-950 border border-orange-200 dark:border-orange-800">
              <div className="text-sm font-semibold text-gray-900 dark:text-white">Your earnings</div>
              <div className="mt-3 space-y-2">
                {(() => {
                  const flatCommission = agreementType === "traditional_paper" ? 0 : (AGREEMENT_COMMISSION[agreementType] || 0);
                  const depositEarnings = depositProtection ? Math.floor(fee * 0.15) : 0;
                  const totalEarnings = flatCommission + depositEarnings;
                  return (
                    <>
                      {agreementType !== "traditional_paper" && (
                        <div className="flex justify-between text-sm"><span className="text-gray-700 dark:text-gray-300">Agreement commission</span><span className="font-medium text-gray-900 dark:text-white">{formatINR(flatCommission)}</span></div>
                      )}
                      {depositProtection && <div className="flex justify-between text-sm"><span className="text-gray-700 dark:text-gray-300">Deposit commission</span><span className="font-medium text-gray-900 dark:text-white">{formatINR(depositEarnings)}</span></div>}
                      {depositEarnings === 0 && agreementType === "traditional_paper" && (
                        <div className="text-xs text-gray-600 dark:text-gray-400 italic">No earnings (deposit not held with Orello)</div>
                      )}
                      {totalEarnings > 0 && (
                        <div className="flex justify-between text-sm border-t border-orange-200 dark:border-orange-700 pt-2 mt-2">
                          <span className="font-semibold text-gray-900 dark:text-white">Total earnings</span>
                          <span className="font-bold text-[#F97316]">{formatINR(totalEarnings)}</span>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              <p className="text-xs text-gray-700 dark:text-gray-300 mt-2.5">
                {agreementType === "traditional_paper"
                  ? depositProtection
                    ? "Deposit commission credited to your wallet when tenant completes deposit payment through Orello."
                    : "No Orello earnings for paper agreements without deposit management."
                  : depositProtection
                  ? "Credited to your wallet when tenant completes deposit payment through Orello."
                  : "Flat agreement commission credited to your wallet immediately after agreement is signed."}
              </p>
            </div>
          )}

          <div className="flex justify-between">
            <button onClick={() => setStep(2)} className="text-sm text-gray-600 inline-flex items-center gap-2"><ArrowLeft className="h-4 w-4" /> Back</button>
            <button onClick={submit} disabled={busy} data-testid="nr-submit" className="bg-[#6366F1] hover:bg-[#4F46E5] btn-press text-white px-6 py-3 rounded-lg font-medium disabled:opacity-50">{busy ? "Creating…" : "Create Rental"}</button>
          </div>
        </div>
      )}

      {/* Share Referral Link Modal */}
      {shareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end z-50" onClick={() => setShareModal(null)}>
          <div
            className="w-full bg-white dark:bg-gray-900 rounded-t-2xl p-6 space-y-4 animate-in slide-in-from-bottom"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Share referral link with {shareModal === "tenant" ? "Tenant" : "Owner"}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Choose how you want to share the registration link
              </p>
            </div>

            <div className="space-y-2">
              <button
                onClick={() => shareReferralLink("whatsapp", shareModal)}
                className="w-full flex items-center gap-3 p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
              >
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center text-xl">💬</div>
                <div className="text-left">
                  <div className="font-medium text-gray-900 dark:text-white">WhatsApp</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">Opens WhatsApp app with message</div>
                </div>
              </button>

              <button
                onClick={() => shareReferralLink("sms", shareModal)}
                className="w-full flex items-center gap-3 p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
              >
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-xl">💌</div>
                <div className="text-left">
                  <div className="font-medium text-gray-900 dark:text-white">SMS</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">Opens messaging app with message</div>
                </div>
              </button>

              <button
                onClick={() => shareReferralLink("email", shareModal)}
                className="w-full flex items-center gap-3 p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
              >
                <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center text-xl">✉️</div>
                <div className="text-left">
                  <div className="font-medium text-gray-900 dark:text-white">Email</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">Opens email client with message</div>
                </div>
              </button>
            </div>

            <button
              onClick={() => setShareModal(null)}
              className="w-full p-3 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white font-medium hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}